import axios from 'axios';
import { apiUrl } from 'src/constants/config';
import { invisiblyApi } from 'src/redux/api/invisibly-api';
import { APITagType } from 'src/redux/api/invisibly-api.types';
import type { InvisiblyApiResponse } from 'src/services/api/api-response.types';
import ReduxService from 'src/redux/redux.service';
import type {
  CampaignEmailSchedule,
  CampaignEmailScheduleWritePayload,
  CampaignEmailSendLog,
  CampaignEmailTestSendRequest,
  ListCampaignEmailSchedulesParams,
} from '../types/campaign-email.types';

const campaignEmailEndpoints = invisiblyApi.injectEndpoints({
  endpoints: (builder) => ({
    getCampaignEmailSchedules: builder.query<CampaignEmailSchedule[], ListCampaignEmailSchedulesParams | void>({
      query: (params) => ({
        method: 'GET',
        url: 'b2b/campaign-emails/schedules',
        params: params ?? {},
        handleErrorGlobally: false,
      }),
      transformResponse(response: InvisiblyApiResponse<CampaignEmailSchedule[]>) {
        return response.item ?? [];
      },
      providesTags: [{ id: 'LIST', type: APITagType.CampaignEmailSchedules }],
    }),
    getCampaignEmailSchedule: builder.query<CampaignEmailSchedule, string>({
      query: (scheduleId) => ({
        method: 'GET',
        url: `b2b/campaign-emails/schedules/${scheduleId}`,
        handleErrorGlobally: false,
      }),
      transformResponse(response: InvisiblyApiResponse<CampaignEmailSchedule>) {
        return response.item;
      },
      providesTags: (_result, _error, scheduleId) => [
        { id: scheduleId, type: APITagType.CampaignEmailSchedules },
      ],
    }),
    createCampaignEmailSchedule: builder.mutation<CampaignEmailSchedule, CampaignEmailScheduleWritePayload>({
      query: (data) => ({
        method: 'POST',
        url: 'b2b/campaign-emails/schedules',
        data,
        handleErrorGlobally: false,
      }),
      transformResponse(response: InvisiblyApiResponse<CampaignEmailSchedule>) {
        return response.item;
      },
      invalidatesTags: [{ id: 'LIST', type: APITagType.CampaignEmailSchedules }],
    }),
    updateCampaignEmailSchedule: builder.mutation<
    CampaignEmailSchedule,
    { scheduleId: string; data: Partial<CampaignEmailScheduleWritePayload> }
    >({
      query: ({ scheduleId, data }) => ({
        method: 'PATCH',
        url: `b2b/campaign-emails/schedules/${scheduleId}`,
        data,
        handleErrorGlobally: false,
      }),
      transformResponse(response: InvisiblyApiResponse<CampaignEmailSchedule>) {
        return response.item;
      },
      invalidatesTags: (_result, _error, { scheduleId }) => [
        { id: 'LIST', type: APITagType.CampaignEmailSchedules },
        { id: scheduleId, type: APITagType.CampaignEmailSchedules },
      ],
    }),
    deleteCampaignEmailSchedule: builder.mutation<{ id: string }, string>({
      query: (scheduleId) => ({
        method: 'DELETE',
        url: `b2b/campaign-emails/schedules/${scheduleId}`,
        handleErrorGlobally: false,
      }),
      transformResponse(response: InvisiblyApiResponse<{ id: string }>) {
        return response.item;
      },
      invalidatesTags: [{ id: 'LIST', type: APITagType.CampaignEmailSchedules }],
    }),
    getCampaignEmailPreview: builder.query<string, string>({
      async queryFn(scheduleId) {
        try {
          const state = ReduxService.Store.getState();
          const jwt = state?.userState?.access;
          const response = await axios.get(
            `${apiUrl}/b2b/campaign-emails/schedules/${scheduleId}/preview`,
            {
              headers: jwt ? { Authorization: `Bearer ${jwt}` } : {},
              responseType: 'text',
              timeout: 120_000,
            }
          );

          if (typeof response.data !== 'string') {
            return { error: { message: 'Preview response was not HTML.' } };
          }

          return { data: response.data };
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to load preview.';
          return { error: { message } };
        }
      },
    }),
    sendCampaignEmailTest: builder.mutation<
    CampaignEmailSendLog,
    { scheduleId: string; body: CampaignEmailTestSendRequest }
    >({
      query: ({ scheduleId, body }) => ({
        method: 'POST',
        url: `b2b/campaign-emails/schedules/${scheduleId}/test-send`,
        data: body,
        handleErrorGlobally: false,
      }),
      transformResponse(response: InvisiblyApiResponse<CampaignEmailSendLog>) {
        return response.item;
      },
    }),
  }),
  overrideExisting: true,
});

export const {
  useGetCampaignEmailSchedulesQuery,
  useGetCampaignEmailScheduleQuery,
  useCreateCampaignEmailScheduleMutation,
  useUpdateCampaignEmailScheduleMutation,
  useDeleteCampaignEmailScheduleMutation,
  useLazyGetCampaignEmailPreviewQuery,
  useSendCampaignEmailTestMutation,
} = campaignEmailEndpoints;
